import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI, authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  User, ChevronLeft, Mail, Shield, CreditCard, LogOut, 
  Loader2, AlertCircle, Check, Crown, Calendar, Trash2, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfileSettingsPage = () => {
  const navigate = useNavigate();
  const { user, token, logout, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  
  // Password change form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will lose access to CV creation and AI features.')) {
      return;
    }
    
    setCancellingSubscription(true);
    try {
      await subscriptionAPI.cancel();
      updateUser({ ...user, is_subscribed: false, subscription_end_date: null });
      toast.success('Subscription cancelled');
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setCancellingSubscription(false);
    }
  };

  const handleResubscribe = async () => {
    navigate('/subscribe');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    if (!deletePassword) {
      toast.error('Please enter your password');
      return;
    }
    
    setDeletingAccount(true);
    try {
      await authAPI.deleteAccount(deletePassword, deleteConfirmation);
      toast.success('Account deleted successfully');
      logout();
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete account';
      toast.error(message);
    } finally {
      setDeletingAccount(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center">
        <div className="max-w-4xl mx-auto w-full flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-slate-600"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Dashboard
          </Button>
          <div className="h-6 w-px bg-slate-200"></div>
          <h1 className="font-semibold text-slate-900">Profile Settings</h1>
        </div>
      </header>

      {/* Content */}
      <main className="py-8 px-4 lg:px-8">
        <div className="max-w-2xl mx-auto space-y-6">
          
          {/* Profile Info */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">{user?.full_name}</h2>
                <p className="text-slate-500 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {user?.email}
                </p>
              </div>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-500 mb-1">Member since</p>
                <p className="font-semibold text-slate-900">{formatDate(user?.created_at)}</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-500 mb-1">Account status</p>
                <p className="font-semibold text-slate-900 flex items-center gap-2">
                  {user?.is_verified ? (
                    <>
                      <Check className="w-4 h-4 text-emerald-500" />
                      Verified
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                      Unverified
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Crown className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Access Status</h3>
            </div>
            
            {user?.is_subscribed ? (
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-emerald-900">Full Access Active</p>
                      <p className="text-sm text-emerald-700">All features unlocked</p>
                    </div>
                    <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
                
                {user?.subscription_end_date && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Calendar className="w-4 h-4" />
                    Access until: {formatDate(user.subscription_end_date)}
                  </div>
                )}
                
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Your data is safe.</strong> Even after your access expires, all your CVs and information are preserved. Simply pay again to continue.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="font-semibold text-amber-900">Access Expired</p>
                  <p className="text-sm text-amber-700">Your CVs are safe! Pay R25 to unlock full access for 30 days.</p>
                </div>
                
                <Button
                  onClick={handleResubscribe}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Get 30 Days Access - R25
                </Button>
              </div>
            )}
          </div>

          {/* Change Password */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-slate-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Change Password</h3>
            </div>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <Label htmlFor="current-password" className="text-slate-700">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="mt-1.5 h-11"
                  required
                />
              </div>
              <div>
                <Label htmlFor="new-password" className="text-slate-700">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                  className="mt-1.5 h-11"
                  required
                />
              </div>
              <div>
                <Label htmlFor="confirm-password" className="text-slate-700">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="mt-1.5 h-11"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={changingPassword}
                className="w-full bg-slate-900 hover:bg-slate-800"
              >
                {changingPassword ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                Update Password
              </Button>
            </form>
          </div>

          {/* Logout */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Log Out
            </Button>
          </div>

          {/* Delete Account */}
          <div className="bg-red-50 rounded-2xl border border-red-200 p-6">
            <h3 className="font-semibold text-red-900 mb-2 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Danger Zone
            </h3>
            <p className="text-sm text-red-700 mb-4">
              Deleting your account will permanently remove all your CVs, job applications, cover letters, and personal data. This action cannot be undone.
            </p>
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-100"
              onClick={() => setShowDeleteModal(true)}
            >
              Delete Account
            </Button>
          </div>
        </div>
      </main>

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Delete Account</h3>
                <p className="text-sm text-slate-500">This action is permanent</p>
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">
                <strong>Warning:</strong> This will permanently delete:
              </p>
              <ul className="text-sm text-red-700 mt-2 space-y-1 list-disc list-inside">
                <li>All your CVs and cover letters</li>
                <li>Job applications and tracking data</li>
                <li>Uploaded documents</li>
                <li>Your account and all personal information</li>
              </ul>
            </div>

            <form onSubmit={handleDeleteAccount} className="space-y-4">
              <div>
                <Label htmlFor="delete-password" className="text-slate-700">
                  Enter your password to confirm
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="delete-password"
                    type={showDeletePassword ? 'text' : 'password'}
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Your password"
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowDeletePassword(!showDeletePassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {showDeletePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="delete-confirmation" className="text-slate-700">
                  Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm
                </Label>
                <Input
                  id="delete-confirmation"
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value.toUpperCase())}
                  placeholder="Type DELETE"
                  className="mt-1.5 font-mono"
                  required
                />
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeletePassword('');
                    setDeleteConfirmation('');
                  }}
                  className="flex-1"
                  disabled={deletingAccount}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={deletingAccount || deleteConfirmation !== 'DELETE' || !deletePassword}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {deletingAccount ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete My Account'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileSettingsPage;
